import { logErrorWithContext } from '../../../utils/sentry/sentrySetup';
import { RootState } from '../../reducers';
import * as nimblyApi from '../../../constants/api';
import * as actions from './siteReport.action';
import { queryStringify } from '../../../utils/string';
import { select, put, call } from 'redux-saga/effects';
import { getFirebase } from 'react-redux-firebase';
import { SiteReportSummary, Questionnaire } from '@nimbly-technologies/nimbly-common';
import { SiteReportState, ReportSummaryLite, GroupedLiteReport } from '../../reducers/siteReport/type';
import moment from 'moment';
import { SagaIterator } from 'redux-saga';

type LitePaginatedPayload<T> = {
  docs?: T[] | Record<string, T>;
  totalPages?: number;
  totalDocs?: number;
  page?: number;
  limit?: number;
};

type LitePaginatedResponse<T> = {
  message?: string;
  data?: LitePaginatedPayload<T>;
  result?: LitePaginatedPayload<T>;
  docs?: T[] | Record<string, T>;
  totalPages?: number;
  totalDocs?: number;
  page?: number;
  limit?: number;
};

const getLitePaginationPayload = <T>(responseBody: LitePaginatedResponse<T>): LitePaginatedPayload<T> => {
  const directPayload = responseBody?.data ?? responseBody?.result;

  if (
    directPayload &&
    typeof directPayload === 'object' &&
    'data' in directPayload &&
    (directPayload as { data?: LitePaginatedPayload<T> }).data
  ) {
    return (directPayload as { data: LitePaginatedPayload<T> }).data;
  }

  return directPayload ?? responseBody ?? {};
};

const normalizeLiteDocs = <T>(docs?: T[] | Record<string, T>): T[] => {
  if (!docs) {
    return [];
  }

  if (Array.isArray(docs)) {
    return docs;
  }

  return Object.values(docs).filter(Boolean);
};

const searchParamsToQuery = (filter: any, limit: number, page: number): any => {
  const postData: any = {
    limit: limit,
    page: page
  };
  postData.sortBy = 'reportID';
  if (filter.sortDirection) {
    postData.sortType = filter.sortDirection;
  }

  if (filter.startDate) {
    postData.startDate = `${moment(filter.startDate).subtract(1, 'days').format('YYYY-MM-DD')} 23:59:00.000Z` || '';
  }
  if (filter.endDate) {
    postData.endDate = `${filter.endDate.format('YYYY-MM-DD')} 23:59:00.000Z` || '';
  }

  if (filter.query && filter.query !== '') {
    postData.search = filter.query;
  }
  return postData;
};

const getLiteDateRange = (
  dateRange: SiteReportState['liteDateRange'],
  customStart: string | null,
  customEnd: string | null
): { startDate: string; endDate: string } => {
  const now = moment();
  const endDate = now.toISOString();
  switch (dateRange) {
    case 'last24h':
      return { startDate: now.clone().subtract(24, 'hours').toISOString(), endDate };
    case 'thisWeek':
      return { startDate: now.clone().startOf('week').toISOString(), endDate };
    case 'lastWeek':
      return {
        startDate: now.clone().subtract(1, 'week').startOf('week').toISOString(),
        endDate: now.clone().subtract(1, 'week').endOf('week').toISOString()
      };
    case 'last7d':
      return { startDate: now.clone().subtract(7, 'days').toISOString(), endDate };
    case 'last30d':
      return { startDate: now.clone().subtract(30, 'days').toISOString(), endDate };
    case 'last90Days':
      return { startDate: now.clone().subtract(90, 'days').toISOString(), endDate };
    case 'last6Months':
      return { startDate: now.clone().subtract(6, 'months').toISOString(), endDate };
    case 'last12Months':
      return { startDate: now.clone().subtract(12, 'months').toISOString(), endDate };
    case 'lastYear':
      return {
        startDate: now.clone().subtract(1, 'year').startOf('year').toISOString(),
        endDate: now.clone().subtract(1, 'year').endOf('year').toISOString()
      };
    case 'monthToDate':
      return { startDate: now.clone().startOf('month').toISOString(), endDate };
    case 'custom':
      return {
        startDate: customStart ? moment(customStart).startOf('day').toISOString() : now.clone().subtract(24, 'hours').toISOString(),
        endDate: customEnd ? moment(customEnd).endOf('day').toISOString() : endDate
      };
    default:
      return { startDate: now.clone().subtract(24, 'hours').toISOString(), endDate };
  }
};

// API expects repeated params (sites=a&sites=b) — queryStringify would emit sites[0]=a, which the API rejects.
const buildRepeatedParams = (key: string, values?: string[]): string =>
  (values ?? [])
    .filter(Boolean)
    .map(v => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
    .join('&');

export function* fetchLiteReports(action: ReturnType<typeof actions.fetchLiteReportsAsync.request>): SagaIterator {
  try {
    const stateSelector = (state: RootState) => state.siteReport;
    const siteReportState: SiteReportState = yield select(stateSelector);
    const reduxFirebase = getFirebase();
    const currentPage = action.payload.clearList ? 1 : siteReportState.liteReportsPage;
    const { startDate, endDate } = getLiteDateRange(
      siteReportState.liteDateRange,
      siteReportState.liteCustomStart,
      siteReportState.liteCustomEnd
    );

    const params: Record<string, any> = {
      limit: 15,
      page: currentPage,
      sortBy: 'scheduleDate',
      sortType: siteReportState.liteSortType,
      startDate,
      endDate
    };
    if (siteReportState.liteFilterQuery) {
      params.search = siteReportState.liteFilterQuery;
    }

    const arrayParams = [
      buildRepeatedParams('sites', siteReportState.liteFilterSites),
      buildRepeatedParams('auditors', siteReportState.liteFilterAuditors)
    ]
      .filter(Boolean)
      .join('&');

    const idToken = (yield call([reduxFirebase.auth().currentUser!, 'getIdToken'])) as string;
    const url = `${nimblyApi.NIMBLY_API_REPORT_SUMMARY}?${queryStringify(params)}${arrayParams ? `&${arrayParams}` : ''}`;
    const request = () => fetch(url, { method: 'GET', headers: { authorization: idToken } });
    const responseRaw: any = (yield call(request)) as Body;
    const responseBody: LitePaginatedResponse<ReportSummaryLite> = (yield responseRaw.json()) as any;

    if ((responseRaw as Response).status >= 400) {
      yield put(actions.fetchLiteReportsAsync.failure({ error: 'Fetch lite reports failed' }));
      return;
    }

    const responseData = getLitePaginationPayload(responseBody);
    const reports: ReportSummaryLite[] = normalizeLiteDocs(responseData?.docs);
    const totalPages: number = responseData?.totalPages ?? 1;
    const combined = action.payload.clearList ? reports : [...siteReportState.liteReports, ...reports];

    yield put(actions.setLiteReportsPage(currentPage + 1));
    yield put(actions.fetchLiteReportsAsync.success({ reports: combined, total: totalPages }));
  } catch (err: any) {
    logErrorWithContext(err, 'saga_fetch_lite_reports_error', {
      saga: 'fetchLiteReports',
      component: 'siteReport.actionSaga.ts'
    });
    yield put(actions.fetchLiteReportsAsync.failure({ error: err?.message ?? 'Unknown error' }));
  }
}

export function* fetchLiteGroupedReports(
  action: ReturnType<typeof actions.fetchLiteGroupedReportsAsync.request>
): SagaIterator {
  try {
    const stateSelector = (state: RootState) => state.siteReport;
    const siteReportState: SiteReportState = yield select(stateSelector);
    const reduxFirebase = getFirebase();
    const currentPage = action.payload.clearList ? 1 : siteReportState.liteReportsPage;
    const { startDate, endDate } = getLiteDateRange(
      siteReportState.liteDateRange,
      siteReportState.liteCustomStart,
      siteReportState.liteCustomEnd
    );

    const params: Record<string, any> = {
      limit: 15,
      page: currentPage,
      sortBy: 'date',
      sortType: siteReportState.liteSortType,
      startDate,
      endDate,
      groupBy: siteReportState.liteGroupBy,
      dataPage: 1,
      dataLimit: 15
    };
    if (siteReportState.liteFilterQuery) {
      params.search = siteReportState.liteFilterQuery;
    }

    const arrayParams = [
      buildRepeatedParams('sites', siteReportState.liteFilterSites),
      buildRepeatedParams('auditors', siteReportState.liteFilterAuditors)
    ]
      .filter(Boolean)
      .join('&');

    const idToken = (yield call([reduxFirebase.auth().currentUser!, 'getIdToken'])) as string;
    const url = `${nimblyApi.NIMBLY_API_REPORT_SUMMARY_GROUP}?${queryStringify(params)}${arrayParams ? `&${arrayParams}` : ''}`;
    const request = () => fetch(url, { method: 'GET', headers: { authorization: idToken } });
    const responseRaw: any = (yield call(request)) as Body;
    const responseBody: LitePaginatedResponse<GroupedLiteReport> = (yield responseRaw.json()) as any;

    if ((responseRaw as Response).status >= 400) {
      yield put(actions.fetchLiteGroupedReportsAsync.failure({ error: 'Fetch grouped reports failed' }));
      return;
    }

    const responseData = getLitePaginationPayload(responseBody);
    const groups: GroupedLiteReport[] = normalizeLiteDocs(responseData?.docs);
    const totalPages: number = responseData?.totalPages ?? 1;
    const combined = action.payload.clearList ? groups : [...siteReportState.liteGroupedReports, ...groups];

    yield put(actions.setLiteReportsPage(currentPage + 1));
    yield put(actions.fetchLiteGroupedReportsAsync.success({ groups: combined, total: totalPages }));
  } catch (err: any) {
    logErrorWithContext(err, 'saga_fetch_lite_grouped_reports_error', {
      saga: 'fetchLiteGroupedReports',
      component: 'siteReport.actionSaga.ts'
    });
    yield put(actions.fetchLiteGroupedReportsAsync.failure({ error: err?.message ?? 'Unknown error' }));
  }
}

export function* fetchSiteReport(action: ReturnType<typeof actions.fetchSiteReportsAsync.request>): SagaIterator {
  try {
    const stateSelector = (state: RootState) => state;
    const stateCurrent: RootState = yield select(stateSelector);
    const siteReportState: SiteReportState = stateCurrent.siteReport;
    const reduxFirebase = getFirebase();
    const currentPage = action.payload.clearList ? 1 : siteReportState.pageIndex;
    const postData = searchParamsToQuery(
      {
        sortDirection: siteReportState.sortDirection,
        startDate: siteReportState.dateType === 'custom' ? siteReportState.customStart : siteReportState.startDate,
        endDate: siteReportState.dateType === 'custom' ? siteReportState.customEnd : siteReportState.endDate,
        query: siteReportState.filterQuery
      },
      50,
      currentPage
    );
    if (reduxFirebase.auth) {
      const idToken = (yield call([reduxFirebase.auth().currentUser!, 'getIdToken'])) as string;
      const request = () =>
        fetch(
          `${nimblyApi.NIMBLY_API_SITE_REPORTS}${action.payload.siteID}?${queryStringify(
            JSON.parse(JSON.stringify(postData))
          )}`,
          {
            method: 'GET',
            headers: {
              authorization: idToken
            }
          }
        );
      const responseRaw: any = (yield call(request)) as Body;
      const responseBody: any = (yield responseRaw.json()) as any;
      yield put(actions.setPageIndexReport(currentPage + 1));
      if (responseRaw.status >= 400) {
        const message = responseBody.message;
        yield put(actions.fetchSiteReportsAsync.failure({ error: message }));
      }
      const responseData: { data: (SiteReportSummary & { questionnaire: Questionnaire })[]; total: number } =
        responseBody.data;
      let combinedReports = responseData?.data || [];
      const currentReports = stateCurrent.siteReport.reports;
      if (action.payload.clearList === false && combinedReports) {
        combinedReports = currentReports.concat(combinedReports);
      }
      let totalData: number = 1;
      if (responseData?.total >= 50) {
        totalData = responseData?.total / 50;
      }
      yield put(actions.fetchSiteReportsAsync.success({ reports: combinedReports, total: Math.ceil(totalData) }));
    }
  } catch (err: any) {
    logErrorWithContext(err, 'saga_fetch_site_report_error', {
      saga: 'fetchSiteReport',
      component: 'siteReport.actionSaga.ts',
      siteID: action?.payload?.siteID
    });
    yield put(actions.fetchSiteReportsAsync.failure({ error: err.message }));
  }
}
